import React, { useRef, useEffect, useState } from "react";
//@ts-ignore
import { BreathingColor } from "@b-design/color";
import TermsContent from "./TermsContent";
import {
  signInWithEmailPwd,
  signUpWithEmailPwd,
  signInWithGoogle,
  resetPassword,
  functions,
} from "../../api/firebase";
import {
  verifyAccessCodeLocally,
  initializePartnerCache,
  getPartnerInfo,
} from "../../api/partners";

import old_logo from "../../assets/images/WeKruit_logo.png";
import logo from "../../assets/images/logo_purple.svg";
import googleIcon from "../../assets/images/googleIcon.svg";
import ErrorDialog from "../ErrorDialog";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";
import { httpsCallable } from "firebase/functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../ui/Dialog";

interface AuthenticationModalProps {
  companyName?: string;
  companyLogo?: string;
  displayName?: string;
  displayInfo?: string;
  logoClassName?: string;
  displayInfoClassName?: string;
}

const AuthenticationModal: React.FC<AuthenticationModalProps> = ({
  companyName,
  companyLogo,
  displayName,
  displayInfo,
  logoClassName,
  displayInfoClassName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = usePartnerAwareNavigate();
  // State for forms
  const [showLogin, setShowLogin] = useState(true);
  const [showSignup, setShowSignup] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isLoadingPartner, setIsLoadingPartner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Enhanced error handling state
  const [errorDialog, setErrorDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "error" as "error" | "warning" | "info",
    // details: "",
    actions: [] as {
      label: string;
      onClick: () => void;
      variant?: "primary" | "secondary" | "danger";
    }[],
  });

  // Error handling function
  const handleError = (
    error: any,
    title: string,
    type: "error" | "warning" | "info" = "error",
    customActions: any[] = []
  ) => {
    console.log("Setting error dialog with:", {
      isOpen: true,
      title,
      message: error.message,
      type,
      actions: customActions,
    });

    // Force a new state object to ensure React picks up the change
    setErrorDialog((prevState) => ({
      ...prevState,
      isOpen: true,
      title,
      message: error.message || "An error occurred",
      type,
      details: error.stack,
      actions: customActions,
    }));
  };

  // Close error dialog
  const closeErrorDialog = () => {
    setErrorDialog((prev) => ({ ...prev, isOpen: false }));
  };

  // Setup BreathingColor background
  useEffect(() => {
    // Don't initialize background if we're showing loading state
    if (!containerRef.current || isLoadingPartner) return;

    const bdColor = new BreathingColor({
      config: {
        background: "e1d3ff",
        palette: ["7ea3ff", "4789ed", "f5f8f9", "91b0ff", "2387e5"],
        offsets: [2, 2, 2, 1.17, 0.65, 0.7, -0.54, 0.9, 2, -0.9],
        twist: [4, 0, 0.37, 5.7, 0.6, 0.15, 1.5, 1, 0.127, 0.05],
        symbolColor: "#c7b2ff",
      },
      container: containerRef.current,
      initWidth: window.innerWidth,
      initHeight: window.innerHeight,
    });

    bdColor.init();

    let rafID: number;
    const animate = () => {
      bdColor.update();
      rafID = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      bdColor.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", onResize);

    const onMouseMove = (e: MouseEvent) => {
      bdColor.setMousemove(e);
    };
    window.addEventListener("mousemove", onMouseMove);

    return () => {
      cancelAnimationFrame(rafID);
      bdColor.destroy();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [isLoadingPartner]);

  // Initialize partner cache if company name is provided
  useEffect(() => {
    const initializePartner = async () => {
      if (companyName) {
        setIsLoadingPartner(true);
        try {
          await initializePartnerCache();
          const partner = getPartnerInfo(companyName);
          if (!partner) {
            handleError(
              new Error("Invalid partner"),
              "Partner Not Found",
              "error"
            );
          }
        } catch (err: any) {
          handleError(err, "Failed to load partner information", "error");
        } finally {
          setIsLoadingPartner(false);
        }
      }
    };

    initializePartner();
  }, [companyName]);

  // Form toggle handlers
  const handleShowLogin = () => {
    setShowLogin(true);
    setShowSignup(false);
    setShowReset(false);
    setHasAgreed(false);
    closeErrorDialog();
  };

  const handleShowSignup = () => {
    setShowLogin(false);
    setShowSignup(true);
    setShowReset(false);
    setHasAgreed(false);
    closeErrorDialog();
  };

  const handleShowReset = () => {
    setShowLogin(false);
    setShowSignup(false);
    setShowReset(true);
    setHasAgreed(false);
    closeErrorDialog();
  };

  // Authentication handlers
  const handleBusinessAuth = async () => {
    if (!hasAgreed) {
      handleError(
        new Error(
          "You must agree to the Terms and Conditions before continuing."
        ),
        "Terms Agreement Required",
        "warning"
      );
      return false;
    }

    if (!companyName || !accessCode) {
      handleError(
        new Error("Please enter the access code provided by your company."),
        "Access Code Required",
        "warning"
      );
      return false;
    }

    try {
      // Use local verification instead of HTTP call
      const isValid = verifyAccessCodeLocally(companyName, accessCode);
      if (!isValid) {
        throw new Error("Invalid access code");
      }
      return true;
    } catch (err: any) {
      handleError(err, "Access Code Verification Failed");
      return false;
    }
  };

  // Show loading state while initializing partner
  if (isLoadingPartner) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[var(--color-primary)]"></div>
      </div>
    );
  }

  // In your AuthenticationModal component
  const handleLogin = async () => {
    if (!hasAgreed) {
      handleError(
        new Error(
          "You must agree to the Terms and Conditions before continuing."
        ),
        "Terms Agreement Required",
        "warning"
      );
      return;
    }

    setIsLoading(true);
    try {
      if (companyName) {
        const authRes = await handleBusinessAuth();
        if (!authRes) {
          setIsLoading(false);
          return;
        }
      }

      console.log("Attempting to sign in...");
      // Wait for the sign in result
      const user = await signInWithEmailPwd(email, password, companyName);
      console.log("Sign in result:", user);
      // Only navigate if we get a successful result
      if (user) {
        navigate("/");
      }
    } catch (err: any) {
      console.log("Login error caught:", err);
      console.log("Error message:", err.message);
      if (err.message.includes("verify your email")) {
        handleError(err, "Email Verification Required", "error");
      } else {
        handleError(err, "Login Failed", "error", [
          {
            label: "Try Again",
            onClick: () => {
              closeErrorDialog();
            },
            variant: "primary",
          },
          {
            label: "Reset Password",
            onClick: () => {
              closeErrorDialog();
              handleShowReset();
            },
            variant: "secondary",
          },
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignUp = async () => {
    if (!hasAgreed) {
      handleError(
        new Error(
          "You must agree to the Terms and Conditions before continuing."
        ),
        "Terms Agreement Required",
        "warning"
      );
      return;
    }

    if (password !== confirmPassword) {
      handleError(
        new Error("Passwords do not match"),
        "Password Mismatch",
        "warning"
      );
      return;
    }

    setIsLoading(true);
    try {
      if (companyName) {
        const authRes = await handleBusinessAuth();
        if (!authRes) {
          setIsLoading(false);
          return;
        }
      }

      console.log("Signing up with email/password...");
      handleError(
        new Error(
          "  Please check your inbox and click the verification link to activate your account. You won't be able to log in until your email is verified."
        ),
        "Email Verification Required",
        "info"
      );
      setTimeout(() => {}, 5000);
      await signUpWithEmailPwd(email, password);
      navigate("/");
    } catch (err: any) {
      handleError(err, "Sign Up Failed", "error", [
        {
          label: "Try Again",
          onClick: () => {
            closeErrorDialog();
            handleSignUp();
          },
          variant: "primary",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (!hasAgreed) {
      handleError(
        new Error(
          "You must agree to the Terms and Conditions before continuing."
        ),
        "Terms Agreement Required",
        "warning"
      );
      return;
    }

    if (companyName) {
      const authRes = await handleBusinessAuth();
      if (!authRes) return;
    }

    try {
      await signInWithGoogle(companyName);
      navigate("/");
    } catch (err: any) {
      handleError(err, "Google Sign In Failed", "error", [
        {
          label: "Try Again",
          onClick: () => {
            closeErrorDialog();
            handleGoogleAuth();
          },
          variant: "primary",
        },
      ]);
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetPassword(resetEmail);
      handleError(
        {
          message: "Please check your email for a password reset link.",
        },
        "Password Reset",
        "info",
        [
          {
            label: "Back to Login",
            onClick: () => {
              closeErrorDialog();
              handleShowLogin();
            },
            variant: "primary",
          },
        ]
      );
    } catch (err: any) {
      handleError(err, "Password Reset Failed", "error", [
        {
          label: "Try Again",
          onClick: () => {
            closeErrorDialog();
            handleResetPassword();
          },
          variant: "primary",
        },
      ]);
    }
  };
  return (
    <div className="relative w-full h-full overflow-hidden font-sans">
      {/* Enhanced Error Dialog */}
      <ErrorDialog
        isOpen={errorDialog.isOpen}
        onClose={closeErrorDialog}
        title={errorDialog.title}
        message={errorDialog.message}
        type={errorDialog.type}
        // details={errorDialog.details}
        actions={errorDialog.actions}
        countdown={5} // Will close after 5 seconds
        showCountdown={true} // Optional, defaults to true
      />
      {/* The full-screen canvas background */}
      <div
        ref={containerRef}
        className="absolute top-0 left-0 w-full h-full z-[-2]"
      />

      {/* Centered Container */}
      <div className="w-screen h-screen flex justify-center items-center bg-transparent">
        {/* The Card */}
        <div className="flex flex-row w-full max-w-[52.5rem] min-h-[31.875rem] bg-white rounded-[8px] shadow-[0_8px_24px_rgba(0,0,0,0.1)] overflow-hidden">
          {/* Left Column */}
          <div className="flex-1 bg-[var(--color-primary)] flex flex-col items-center justify-center p-8">
            {/* Branding */}
            <div className="flex flex-col items-center mb-4">
              <img
                src={companyName && companyLogo ? companyLogo : logo}
                alt="WeKruit Logo"
                className={`flex-shrink-0 rounded-[6px] mb-[30px] ${
                  companyName && companyLogo
                    ? logoClassName || "w-auto bg-white px-4 py-2 h-[4.75rem]"
                    : "h-[3.75rem] bg-white px-4 py-2"
                }`}
              />
              <h1 className="text-white text-center text-[32px] font-bold leading-[40px] m-0">
                {companyName && companyLogo ? displayName : "WeKruit"}
              </h1>
            </div>
            <p
              className={
                companyName && displayInfoClassName
                  ? displayInfoClassName
                  : "text-white text-center text-[12px] font-normal leading-[20px] max-w-[280px] m-0"
              }
            >
              {companyName && displayInfo
                ? displayInfo
                : `WeKruit uses AI trained by MBB interviewers to
                            deliver realistic consulting mock interviews with
                            customized feedback, ensuring your success.`}
            </p>
          </div>

          {/* Right Column */}
          <div className="flex-1 flex flex-col justify-center bg-white p-8">
            {/* ---- LOGIN FORM ---- */}
            {showLogin && !showSignup && !showReset && (
              <>
                <h2 className="text-[var(--color-primary)] text-center text-[32px] font-bold leading-[40px] mb-6">
                  Welcome
                </h2>

                {/* Google Auth */}
                <button
                  onClick={handleGoogleAuth}
                  className="flex items-center justify-center gap-2 border border-[#d4d6db] bg-white text-[#81848f] w-[248px] h-[34px] flex-shrink-0 rounded-[2px] text-[14px] cursor-pointer mx-auto"
                >
                  <img
                    src={googleIcon}
                    alt="Google Icon"
                    className="w-[18px] h-[18px] inline-block rounded-[2px]"
                  />
                  Sign in with Google
                </button>

                {/* or Separator */}
                <div className="flex items-center w-[264px] my-2 mx-auto">
                  <div className="flex-1 border-b border-[#d4d6db] ml-[0.5rem]" />
                  <span className="mx-2 text-[#474a52] text-[14px]">or</span>
                  <div className="flex-1 border-b border-[#d4d6db] mr-[0.5rem]" />
                </div>

                {/* Email */}
                <div className="mb-4 flex justify-center">
                  <input
                    id="login-email"
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                  />
                </div>

                {/* Password */}
                <div className="mb-4 flex justify-center">
                  <input
                    id="login-password"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                  />
                </div>

                {/* Access Code */}
                {companyName && (
                  <div className="mb-4 flex justify-center">
                    <input
                      id="login-access-code"
                      type="text"
                      placeholder="Access Code"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                    />
                  </div>
                )}

                {/* Sign In Button */}
                <button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="block w-[248px] h-[34px] rounded-[2px] text-white text-[14px] cursor-pointer mx-auto mb-4 relative disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "radial-gradient(266.28% 88.31% at 97.2% 100%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), var(--color-primary)",
                  }}
                >
                  {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    "Sign in"
                  )}
                </button>

                {/* Homepage Button */}
                <button className="block w-[248px] h-[34px] rounded-[2px] border border-[#d4d6db] bg-white text-[var(--color-primary)] text-[14px] cursor-pointer mx-auto mb-4">
                  Homepage
                </button>
                {/* Terms & Conditions Checkbox */}
                <div className="mb-2 flex justify-center mb-[2rem]">
                  <label className="flex items-center text-[12px] text-[#81848f]">
                    <input
                      type="checkbox"
                      checked={hasAgreed}
                      onChange={(e) => setHasAgreed(e.target.checked)}
                      className="mr-2 border-[#D4D6DB] accent-[var(--color-primary)]"
                    />
                    <span>
                      Agree with{" "}
                      <button
                        type="button"
                        onClick={() => navigate("/terms")}
                        className="cursor-pointer text-[var(--color-primary)] bg-none border-none no-underline hover:underline"
                      >
                        Terms of Service
                      </button>{" "}
                      and{" "}
                      <button
                        type="button"
                        onClick={() => navigate("/privacy")}
                        className="cursor-pointer text-[var(--color-primary)] bg-none border-none no-underline hover:underline"
                      >
                        Privacy Policy
                      </button>
                    </span>
                  </label>
                </div>

                {/* Error Message
                                {error && (
                                    <div className="text-red-500 m-auto mb-4 text-[0.825rem] text-center max-w-[248px]">
                                        {error}
                                    </div>
                                )} */}

                {/* Bottom Links */}
                <div className="flex flex-col items-center">
                  <div className="text-[#81848f] text-[12px] font-normal leading-[20px] mb-2">
                    Don't have an account?
                    <a
                      href="#"
                      onClick={handleShowSignup}
                      className="text-[var(--color-primary)] hover:underline ml-1"
                    >
                      Sign up
                    </a>
                  </div>
                  <div className="text-[#81848f] text-[12px] font-normal leading-[20px]">
                    Forgot your password?
                    <a
                      href="#"
                      onClick={handleShowReset}
                      className="text-[var(--color-primary)] hover:underline ml-1"
                    >
                      Reset it
                    </a>
                  </div>
                </div>
              </>
            )}

            {/* ---- SIGN-UP FORM ---- */}
            {showSignup && !showLogin && !showReset && (
              <>
                <h2 className="text-[var(--color-primary)] text-center text-[32px] font-bold leading-[40px] mb-6">
                  Create an Account
                </h2>

                <button
                  onClick={handleGoogleAuth}
                  className="flex items-center justify-center gap-2 border border-[#d4d6db] bg-white text-[#81848f] w-[248px] h-[34px] flex-shrink-0 rounded-[2px] text-[14px] cursor-pointer mx-auto"
                >
                  <img
                    src={googleIcon}
                    alt="Google Icon"
                    className="w-[18px] h-[18px] inline-block rounded-[2px]"
                  />
                  Sign up with Google
                </button>

                {/* or separator */}
                <div className="flex items-center w-[264px] my-2 mx-auto">
                  <div className="flex-1 border-b border-[#d4d6db]" />
                  <span className="mx-2 text-[#d4d6db] text-[14px]">or</span>
                  <div className="flex-1 border-b border-[#d4d6db]" />
                </div>

                <div className="mb-4 flex justify-center">
                  <input
                    id="signup-email"
                    type="email"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                  />
                </div>

                <div className="mb-4 flex justify-center">
                  <input
                    id="signup-password"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                  />
                </div>

                <div className="mb-4 flex justify-center">
                  <input
                    id="signup-confirm"
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                  />
                </div>

                {/* Access Code */}
                {companyName && (
                  <div className="mb-4 flex justify-center">
                    <input
                      id="login-access-code"
                      type="text"
                      placeholder="Access Code"
                      value={accessCode}
                      onChange={(e) => setAccessCode(e.target.value)}
                      className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                    />
                  </div>
                )}

                <button
                  onClick={handleSignUp}
                  disabled={isLoading}
                  className="block w-[248px] h-[34px] rounded-[2px] text-white text-[14px] cursor-pointer mx-auto mb-4 relative disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "radial-gradient(266.28% 88.31% at 97.2% 100%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), var(--color-primary)",
                  }}
                >
                  {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    "Sign up"
                  )}
                </button>

                <button className="block w-[248px] h-[34px] rounded-[2px] border border-[#d4d6db] bg-white text-[var(--color-primary)] text-[14px] cursor-pointer mx-auto mb-4">
                  Homepage
                </button>
                {/* Terms & Conditions Checkbox */}
                <div className="mb-4 flex justify-center">
                  <label className="flex items-center text-[12px] text-[#81848f] no-underline w-[248px]">
                    <input
                      type="checkbox"
                      checked={hasAgreed}
                      onChange={(e) => setHasAgreed(e.target.checked)}
                      className="mr-2 border-[#D4D6DB] accent-[var(--color-primary)]"
                    />
                    <span>
                      Agree with{" "}
                      <Dialog>
                        <DialogTrigger asChild>
                          <button
                            type="button"
                            className="cursor-pointer text-[var(--color-primary)] no-underline bg-none border-none underline"
                          >
                            Terms and Condition
                          </button>
                        </DialogTrigger>{" "}
                        and{" "}
                        <DialogTrigger asChild>
                          <button
                            type="button"
                            className="cursor-pointer text-[var(--color-primary)] no-underline bg-none border-none underline"
                          >
                            Privacy Policy
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[40vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle>Terms and Conditions</DialogTitle>
                          </DialogHeader>
                          <DialogDescription>
                            <TermsContent />
                          </DialogDescription>
                        </DialogContent>
                      </Dialog>
                    </span>
                  </label>
                </div>

                {/* {error && (
                                    <div className="text-red-500 m-auto mb-4 text-[0.825rem] text-center max-w-[248px]">
                                        {error}
                                    </div>
                                )} */}
                <div className="flex flex-col items-center">
                  <div className="text-[#81848f] text-[12px] font-normal leading-[20px]">
                    Already have an account?
                    <a
                      href="#"
                      onClick={handleShowLogin}
                      className="text-[var(--color-primary)] hover:underline ml-1"
                    >
                      Sign in
                    </a>
                  </div>
                </div>
              </>
            )}

            {/* ---- RESET PASSWORD FORM ---- */}
            {showReset && !showLogin && !showSignup && (
              <>
                <h2 className="text-[var(--color-primary)] text-center text-[32px] font-bold leading-[40px] mb-6">
                  Reset Password
                </h2>

                <div className="text-center mb-6">
                  <p className="text-gray-700 text-[14px] mb-2">
                    Forgot your password? Don't worry, we'll help you reset it.
                  </p>
                  <p className="text-gray-600 text-[14px]">
                    Enter your email address below and we'll send you
                    instructions to reset your password.
                  </p>
                </div>

                <div className="mb-4 flex justify-center">
                  <input
                    id="reset-email"
                    type="email"
                    placeholder="Email Address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-[248px] h-[30px] rounded-[2px] border border-[#d4d6db] bg-white px-2 text-[14px]"
                  />
                </div>

                <button
                  onClick={handleResetPassword}
                  className="block w-[248px] h-[34px] rounded-[2px] bg-[radial-gradient(266.28%_88.31%_at_97.2%_100%,_rgba(255,255,255,0.2)_0%,_rgba(255,255,255,0)_100%),_var(--color-primary)] text-white text-[14px] cursor-pointer mx-auto mb-4"
                >
                  Reset Password
                </button>

                <div className="text-center mb-4">
                  <p className="text-gray-600 text-[12px]">
                    After submitting, please check your email inbox for reset
                    instructions. If you don't receive an email within a few
                    minutes, check your spam folder.
                  </p>
                </div>

                <button
                  className="block w-[248px] h-[34px] rounded-[2px] border border-[#d4d6db] bg-white text-[var(--color-primary)] text-[14px] cursor-pointer mx-auto mb-4"
                  onClick={handleShowLogin}
                >
                  Back to Login
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationModal;
